import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import useNavigate from "src/hooks/useNavigate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { NotificationAlert } from "../../../components/common";
import { DatePickerFormInputFabric, SelectFormInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupPersonnelCreateBreadcrumbs, GroupPersonnelDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { GroupPersonnelSubMenu } from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms";
import { ITrialPersonnelReduxFormState } from "../../../reducers/reactReduxForms/trialPersonnel";

import "./GroupPersonnelPage.scss";

interface GroupPersonnelPageProps {
    form: Dtos.TrialPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialPersonnelReduxFormState;
    trialPersonnel: Dtos.TrialPersonnel;
    group: Dtos.Group;
    lookups: Dtos.Lookup[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    savingTrialPersonnel: boolean;
    saveTrialPersonnelSuccess: boolean;
    saveTrialPersonnelFailure: boolean;
    creatingTrialPersonnel: boolean;
    createTrialPersonnelSuccess: boolean;
    createTrialPersonnelFailure: boolean;
    validatingTrialPersonnel: boolean;
    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelLoadByIdActionCreator;
    saveTrialPersonnel: TrialPersonnelActions.ITrialPersonnelSaveActionCreator;
    createTrialPersonnel: TrialPersonnelActions.ITrialPersonnelCreateActionCreator;
    validateTrialPersonnel: TrialPersonnelActions.ITrialPersonnelValidateActionCreator;
    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialPersonnel";

const GroupPersonnelPage: FC<GroupPersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    trialPersonnel,
    group,
    lookups,
    loadingTrialPersonnel,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    savingTrialPersonnel,
    saveTrialPersonnelSuccess,
    saveTrialPersonnelFailure,
    creatingTrialPersonnel,
    validatingTrialPersonnel,
    loadingGroup,
    loadGroupFailure,
    loadGroupErrorCode,
    validationFailures,
    permissions,
    clearTrialPersonnel,
    loadTrialPersonnel,
    saveTrialPersonnel,
    createTrialPersonnel,
    validateTrialPersonnel,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadGroup,
    clearGroup
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const { trialPersonnelId } = useParams<{ trialPersonnelId: string }>();
    const groupIdNumber = parseInt(groupId, 10);
    const trialPersonnelIdNumber = parseInt(trialPersonnelId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Personnel
    useEffect(() => {
        if (groupIdNumber && trialPersonnelIdNumber && !trialPersonnel) {
            setDrawerItems(
                GroupPersonnelSubMenu(
                    groupIdNumber,
                    trialPersonnelIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                GroupPersonnelDetailsBreadcrumbs(
                    groupIdNumber,
                    trialPersonnelIdNumber
                )
            );

            loadTrialPersonnel(trialPersonnelIdNumber);
            loadGroup(groupIdNumber);
        } else if (
            groupIdNumber &&
            !trialPersonnelIdNumber &&
            !trialPersonnel
        ) {
            setBreadcrumbs(
                GroupPersonnelCreateBreadcrumbs(groupIdNumber)
            );
            setDrawerItems([]);
            createTrialPersonnel(null, null, groupIdNumber);
            loadGroup(groupIdNumber);
        } else if (trialPersonnel && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        groupIdNumber,
        trialPersonnelIdNumber,
        trialPersonnel,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        GroupPersonnelSubMenu,
        GroupPersonnelDetailsBreadcrumbs,
        GroupPersonnelCreateBreadcrumbs,
        createTrialPersonnel,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadGroupFailure,
        loadTrialPersonnelFailure,
        loadGroupErrorCode,
        loadTrialPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            groupIdNumber &&
            trialPersonnelIdNumber &&
            group &&
            trialPersonnel
        ) {
            setBreadcrumbs(
                GroupPersonnelDetailsBreadcrumbs(
                    groupIdNumber,
                    trialPersonnelIdNumber,
                    group?.name,
                    trialPersonnel?.personnel?.displayWithSalutation
                )
            );
        } else if (
            groupIdNumber &&
            !trialPersonnelIdNumber &&
            group &&
            !trialPersonnel
        ) {
            setBreadcrumbs(
                GroupPersonnelCreateBreadcrumbs(
                    groupIdNumber,
                    group?.name
                )
            );
        }
    }, [
        groupIdNumber,
        trialPersonnelIdNumber,
        group,
        trialPersonnel,
        setBreadcrumbs,
        GroupPersonnelDetailsBreadcrumbs,
        GroupPersonnelCreateBreadcrumbs,
    ]);

    // Reload trial personnel after saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/groups/${groupIdNumber}/personnel`);
            } else if (!trialPersonnelIdNumber) {
                navigate(
                    `/groups/${groupIdNumber}/personnel/${trialPersonnel?.id}/details`
                );
                clearTrialPersonnel();
                clearGroup();
            }
        }
    }, [
        savingTrialPersonnel,
        saveTrialPersonnelSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialPersonnel, saveTrialPersonnelFailure]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearTrialPersonnel();
            clearGroup();
            resetForm(reduxFormName);
        };
    }, [clearTrialPersonnel, clearGroup, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialPersonnel = {
                trialId: null,
                institutionId: null,
                groupLevelTypeId: 2,
            } as Dtos.TrialPersonnel;

            if (!data.groupId) {
                changes.groupId = groupIdNumber;
            }

            await saveTrialPersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/groups/${groupIdNumber}/personnel`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialPersonnel }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

            if (form && hasBeenSubmitted && !validatingTrialPersonnel) {
                await validateTrialPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialPersonnelIdNumber,
                permissions,
                Dtos.Permission.PersonnelUpdate,
                Dtos.Permission.PersonnelCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName: "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-lg-1",
                                                fieldClassName:
                                                    "col-sm-12 col-lg-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.personnelId,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-xl-12 col-xxl-2",
                                                fieldClassName:
                                                    "col-xl-12 col-xxl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialPersonnel",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        trialPersonnelIdNumber,
        permissions,
        savingTrialPersonnel,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelView}
            loading={
                loadingTrialPersonnel ||
                savingTrialPersonnel ||
                creatingTrialPersonnel ||
                loadingGroup
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<GroupPersonnelPageProps> => {
    let trialPersonnel: Dtos.TrialPersonnel | undefined = !(
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialPersonnel: trialPersonnel!,
        group: group!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        savingTrialPersonnel:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Pending,
        saveTrialPersonnelSuccess:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Success,
        saveTrialPersonnelFailure:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Failure,
        creatingTrialPersonnel:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Pending,
        createTrialPersonnelSuccess:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Success,
        createTrialPersonnelFailure:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Failure,
        validatingTrialPersonnel:
            state.trialPersonnel.validateState &&
            state.trialPersonnel.validateState.status == RequestState.Pending,
        loadingGroup:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialPersonnel.lookups,
        form: state.reduxForms.trialPersonnel,
        formState: state.trialPersonnel.formState,
        formProperties: state.trialPersonnel.formProperties,
        reduxFormState: state.reduxForms.formState.trialPersonnel,
        validationFailures: trialPersonnel
            ? state.trialPersonnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<GroupPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelById,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.Clear,
            dispatch
        ),
        saveTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.SaveTrialPersonnel,
            dispatch
        ),
        createTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.CreateTrialPersonnel,
            dispatch
        ),
        validateTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.ValidateTrialPersonnel,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadGroup: bindActionCreators(
            GroupActions.LoadGroupById,
            dispatch
        ),
        clearGroup: bindActionCreators(
            GroupActions.Clear,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupPersonnelPage);
